import { useMemo } from 'react';
import { createSafeContext } from '@mews/framework';
import { WithChildren } from '@optimus-web/common';
import { PaymentDetailResult, PaymentMethodDetailResult, PaymentMethod } from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';

import { useGetPaymentDetails, usePaymentMethodDetails } from '../queries';
import { usePaymentPlanDetails } from '../hooks';

interface PaymentDetailsSidebarContextProps extends WithAccountId {
    paymentId: string;
}

interface PaymentDetailsSidebarContextValue {
    accountId: PaymentDetailsSidebarContextProps['accountId'];
    paymentDetails?: PaymentDetailResult['details'];
    receipt?: PaymentDetailResult['receipt'];
    paymentMethodDetails?: PaymentMethodDetailResult;
    isFetching: boolean;
    isError: boolean;
    isPaymentMethodDetailsFetching: boolean;
    isPaymentMethodDetailsError: boolean;
    paymentPlanId?: string;
    paymentPlanName?: string;
}

const { PaymentDetailsSidebarContextProvider: ContextProvider, usePaymentDetailsSidebarContext } = createSafeContext<
    'PaymentDetailsSidebarContext',
    PaymentDetailsSidebarContextValue
>('PaymentDetailsSidebarContext', {
    accountId: '',
    paymentDetails: undefined,
    receipt: undefined,
    paymentMethodDetails: undefined,
    isFetching: true,
    isError: false,
    isPaymentMethodDetailsFetching: true,
    isPaymentMethodDetailsError: false,
    paymentPlanId: undefined,
    paymentPlanName: undefined,
});

export const PaymentDetailsSidebarContextProvider = ({
    children,
    paymentId,
    accountId,
}: PaymentDetailsSidebarContextProps & WithChildren) => {
    const { data, isFetching, isError } = useGetPaymentDetails({ paymentId });

    const paymentMethodMinimalDetails = data?.details?.paymentMethodDetails;

    const isPaymentMethodQueryEnabled = !!(
        paymentMethodMinimalDetails?.paymentMethodId &&
        paymentMethodMinimalDetails?.paymentMethodName &&
        accountId
    );

    const {
        data: paymentMethodDetails,
        isFetching: isPaymentMethodDetailsFetching,
        isError: isPaymentMethodDetailsError,
    } = usePaymentMethodDetails(
        {
            id: paymentMethodMinimalDetails?.paymentMethodId ?? '',
            accountId: accountId ?? '',
            paymentMethod: paymentMethodMinimalDetails?.paymentMethodName as PaymentMethod,
        },
        {
            enabled: isPaymentMethodQueryEnabled,
        }
    );

    const { paymentPlanId, paymentPlanName } = usePaymentPlanDetails({
        paymentMethodDetailResult: paymentMethodDetails,
    });

    const contextValue: PaymentDetailsSidebarContextValue = useMemo(
        () => ({
            accountId,
            paymentDetails: data?.details,
            receipt: data?.receipt,
            paymentMethodDetails,
            isFetching,
            isError,
            isPaymentMethodDetailsFetching,
            isPaymentMethodDetailsError,
            paymentPlanId: paymentPlanId ?? undefined,
            paymentPlanName: paymentPlanName ?? undefined,
        }),
        [
            accountId,
            data,
            paymentMethodDetails,
            isFetching,
            isError,
            isPaymentMethodDetailsFetching,
            isPaymentMethodDetailsError,
            paymentPlanId,
            paymentPlanName,
        ]
    );
    return <ContextProvider value={contextValue}>{children}</ContextProvider>;
};

export { usePaymentDetailsSidebarContext };
